import Storage from 'vue-ls'
import { defineStore } from 'pinia'
import { mixAuthApi } from '@/api/index'
import type { LoginModel } from '@/api/swagger'
import Vue from 'vue'
import { GlobalActions } from '../../../../enums/globa'
import type { LoginUserOutputAuthResult } from '@/api/swagger-mix-auth'

export const { ls } = Storage.useStorage({
  namespace: 'vue_mix_auth_', // key prefix
  name: 'ls', // name variable Vue.[ls] or this.[$ls],
  storage: 'local', // storage name session, local, memory
})

export enum ModuleType {
  /**
   * 消防审验
   */
  FireDesignReview = 101,
  /**
   * 勘察审图
   */
  SurveyDrawing = 102,
  /**
   * 专家库
   */
  ExpertLibrary = 103,
  /**
   * 电动车
   */
  ElectricBicycle = 104,
  /**
   * 专项整治
   */
  SpecialRectification = 105,
}

interface IMixAuthState {
  userName: string
  token: string
  modules: ModuleType[]
}

export const useMixAuthStore = defineStore('useMixAuthStore', {
  state: (): IMixAuthState => {
    return {
      userName: ls.get('userName') || '',
      token: ls.get('token') || '',
      modules:
        (ls.get('modules') || '')
          .split(',')
          .filter((v) => v)
          .map((v) => Number(v)) || [],
    }
  },
  actions: {
    init(payload) {
      this.token = payload.mixAuthToken
      this.userName = payload.mixAuthUserName
      this.modules = payload.mixAuthModules

      ls.set('token', this.token || '')
      ls.set('userName', this.userName || '')
      ls.set('modules', this.modules.join(',') || '')
    },
    mixAuthLogin(loginModel: LoginModel) {
      return mixAuthApi.post('/api/Account/Login', loginModel).then(async (res) => {
        await this.handlerMixAuthResult(res.data)
        return res.data
      })
    },
    handlerMixAuthResult(data: LoginUserOutputAuthResult) {
      return new Promise(async (resolve, reject) => {
        try {
          const { access_token } = data
          if (access_token && data.user) {
            this.token = access_token

            await this.getUserInfo()

            if (window.__POWERED_BY_QIANKUN__) {
              Vue.prototype.$setGlobalState({
                action: GlobalActions.SetMixAuth,
                mixAuthUserName: this.userName,
                mixAuthToken: this.token,
                mixAuthModules: this.modules,
              })
              return resolve(true)
            }
            return resolve(true)
          }
        } catch (error) {
          reject(error)
        }
      })
    },
    getUserInfo() {
      return mixAuthApi
        .get('/api/UserInfo/DetailData', { headers: { Authorization: `Bearer ${this.token}` } })
        .then((res) => {
          this.init({
            mixAuthToken: this.token,
            mixAuthUserName: res.data.realName,
            mixAuthModules: (res.data.module || '')
              .split(',')
              .filter((v) => v)
              .map((v) => Number(v)),
          })

          return res.data
        })
    },
    getLoginCode() {
      return mixAuthApi
        .get('/api/UserInfo/GetLoginCode', { headers: { Authorization: `Bearer ${this.token}` } })
        .then((res) => res.data)
    },
    addModule(moduleType: ModuleType) {
      if (this.modules.includes(moduleType)) return Promise.resolve()

      return mixAuthApi
        .post(
          '/api/UserInfo/AddModule',
          { module: moduleType.toString() },
          { headers: { Authorization: `Bearer ${this.token}` } },
        )
        .then(async (res) => {
          await this.getUserInfo()
          return res
        })
    },
    hasModule(moduleType: ModuleType | ModuleType[]) {
      if (Array.isArray(moduleType)) {
        return moduleType.some((v) => this.modules.includes(v))
      } else {
        return this.modules.includes(moduleType)
      }
    },
    clear() {
      ls.clear()
      this.userName = ''
      this.token = ''
      this.modules = []
    },
    getToken() {
      return ls.get('token')
    },
  },
  getters: {
    getUserName: (state) => state.userName,
  },
})
